/**
 * Enhanced Theme Provider with Multi-Theme Support
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { ConfigProvider } from 'antd';
import { ThemeConfig, ThemeMode, ThemeContextType, UserThemeSettings, ThemeSelection } from './types';
import { getTheme, getAllThemes, DEFAULT_THEME_NAME, DEFAULT_THEME_MODE } from './themes';
import { resolveThemeSelection, resolveThemeForUser } from './theme-resolver';
import { apiService } from '../services/api';
import { useAuthStore } from '../store/auth';

const ThemeContext = createContext<ThemeContextType | null>(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within an EnhancedThemeProvider');
  }
  return context;
};

interface EnhancedThemeProviderProps {
  children: React.ReactNode;
}

export const EnhancedThemeProvider: React.FC<EnhancedThemeProviderProps> = ({ children }) => {
  const { user } = useAuthStore();
  const [currentTheme, setCurrentTheme] = useState<ThemeConfig>(getTheme(DEFAULT_THEME_NAME));
  const [themeMode, setThemeMode] = useState<ThemeMode>(DEFAULT_THEME_MODE);
  const [themeSelection, setThemeSelection] = useState<ThemeSelection>('auto');
  const [isThemeLoaded, setIsThemeLoaded] = useState(false);
  const [userSettings, setUserSettings] = useState<UserThemeSettings>({
    themeName: 'auto',
    themeMode: DEFAULT_THEME_MODE,
  });
  const [availableThemes] = useState<ThemeConfig[]>(getAllThemes());

  // Apply theme variables to the document
  const applyThemeVariables = useCallback((theme: ThemeConfig, mode: ThemeMode) => {
    const root = document.documentElement;
    
    // Remove existing theme attributes
    root.removeAttribute('data-theme');
    root.removeAttribute('data-theme-name');
    
    // Apply base theme variables
    Object.entries(theme.cssVariables).forEach(([property, value]) => {
      root.style.setProperty(property, value);
    });
    
    // Apply mode-specific overrides
    let isDark = false;
    switch (mode) {
      case 'dark':
        if (theme.darkMode) {
          Object.entries(theme.darkMode).forEach(([property, value]) => {
            root.style.setProperty(property, value);
          });
        }
        root.setAttribute('data-theme', 'dark');
        isDark = true;
        break;
      case 'high-contrast':
        root.setAttribute('data-theme', 'high-contrast');
        break;
      case 'auto':
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        if (prefersDark && theme.darkMode) {
          Object.entries(theme.darkMode).forEach(([property, value]) => {
            root.style.setProperty(property, value);
          });
          root.setAttribute('data-theme', 'dark');
          isDark = true;
        }
        break;
      default:
        // Light mode - base variables are already applied
        break;
    }
    
    // Set theme name attribute for CSS targeting
    root.setAttribute('data-theme-name', theme.id);
    
    return isDark;
  }, []);

  // Set theme by selection (supports 'auto', 'silver-falls-capital', 'sportworks')
  const setTheme = useCallback((themeSelectionId: ThemeSelection) => {
    console.log('Manual theme change to:', themeSelectionId);
    const resolvedTheme = resolveThemeSelection(themeSelectionId, user);
    console.log('Resolved theme:', resolvedTheme.name);
    setCurrentTheme(resolvedTheme);
    setThemeSelection(themeSelectionId);
    setUserSettings(prev => ({ ...prev, themeName: themeSelectionId }));
    applyThemeVariables(resolvedTheme, themeMode);
  }, [themeMode, applyThemeVariables, user]);

  // Set theme mode
  const setMode = useCallback((mode: ThemeMode) => {
    setThemeMode(mode);
    setUserSettings(prev => ({ ...prev, themeMode: mode }));
    applyThemeVariables(currentTheme, mode);
    
    // Save mode to localStorage for immediate persistence
    localStorage.setItem('ai-workhub-theme-mode', mode);
  }, [currentTheme, applyThemeVariables]);

  // Set workspace-specific theme override
  const setWorkspaceThemeOverride = useCallback((
    workspaceId: string,
    settings: {
      themeName?: string;
      themeMode?: ThemeMode;
      customSettings?: Record<string, any>;
    }
  ) => {
    setUserSettings(prev => ({
      ...prev,
      workspaceOverrides: {
        ...prev.workspaceOverrides,
        [workspaceId]: settings
      }
    }));
  }, []);

  // Save theme settings to database
  const saveThemeSettings = useCallback(async () => {
    try {
      await apiService.saveUserThemeSettings(userSettings);
    } catch (error) {
      console.error('Failed to save theme settings:', error);
      // Keep localStorage as fallback
      localStorage.setItem('ai-workhub-theme-settings', JSON.stringify(userSettings));
    }
  }, [userSettings]);

  // Load theme settings from database
  const loadThemeSettings = useCallback(async () => {
    console.log('Loading theme settings...');
    try {
      const settings = await apiService.getUserThemeSettings();
      if (settings) {
        console.log('Loaded theme settings from database:', settings);
        setUserSettings(settings);
        setThemeSelection(settings.themeName as ThemeSelection);
        
        // Apply the loaded theme (will resolve auto themes when user is available)
        if (settings.themeName === 'auto') {
          // For auto theme, wait for user to be available in the separate effect
          setThemeMode(settings.themeMode);
        } else {
          // For specific themes, apply immediately
          const specificTheme = getTheme(settings.themeName);
          setCurrentTheme(specificTheme);
          setThemeMode(settings.themeMode);
          applyThemeVariables(specificTheme, settings.themeMode);
        }
        setIsThemeLoaded(true);
        return;
      }
    } catch (error) {
      // Don't log error for authentication issues - they're handled elsewhere
      if (error instanceof Error && error.message === 'Authentication required') {
        console.log('Skipping theme settings load - authentication required');
      } else {
        console.error('Failed to load theme settings from database:', error);
      }
    }
    
    // Fallback to localStorage or defaults
    try {
      const savedSettings = localStorage.getItem('ai-workhub-theme-settings');
      const savedMode = localStorage.getItem('ai-workhub-theme-mode');
      
      if (savedSettings) {
        const settings = JSON.parse(savedSettings);
        console.log('Loaded theme settings from localStorage:', settings);
        setUserSettings(settings);
        setThemeSelection(settings.themeName as ThemeSelection);
        
        if (settings.themeName === 'auto') {
          setThemeMode(settings.themeMode);
        } else {
          const specificTheme = getTheme(settings.themeName);
          setCurrentTheme(specificTheme);
          setThemeMode(settings.themeMode);
          applyThemeVariables(specificTheme, settings.themeMode);
        }
      } else if (savedMode) {
        setThemeMode(savedMode as ThemeMode);
      } else {
        // Apply defaults - auto theme will be resolved when user is available
        console.log('Applying default settings');
        setThemeSelection('auto');
        setThemeMode(DEFAULT_THEME_MODE);
      }
    } catch (error) {
      console.error('Failed to load theme settings from localStorage:', error);
      // Final fallback
      setThemeSelection('auto');
      setThemeMode(DEFAULT_THEME_MODE);
    }
    setIsThemeLoaded(true);
  }, [applyThemeVariables]);

  // Initialize theme on mount - only run once
  useEffect(() => {
    loadThemeSettings();
  }, []); // Empty dependency array - only run on mount

  // Listen for system theme changes in auto mode
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemThemeChange = () => {
      if (themeMode === 'auto') {
        applyThemeVariables(currentTheme, 'auto');
      }
    };

    mediaQuery.addEventListener('change', handleSystemThemeChange);

    return () => {
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    };
  }, [themeMode, currentTheme, applyThemeVariables]);

  // Auto-update theme when user changes AND we're in auto mode
  useEffect(() => {
    if (themeSelection === 'auto' && user && user.companyName) {
      console.log('Auto theme update for user company:', user.companyName);
      const resolvedTheme = resolveThemeForUser(user);
      console.log('Resolved theme:', resolvedTheme.name);
      if (resolvedTheme.id !== currentTheme.id) {
        console.log('Applying new theme:', resolvedTheme.name);
        setCurrentTheme(resolvedTheme);
        applyThemeVariables(resolvedTheme, themeMode);
      }
    }
  }, [user?.companyName, themeSelection, currentTheme.id, themeMode, applyThemeVariables]);

  // Auto-save settings when they change
  useEffect(() => {
    if (userSettings.themeName !== DEFAULT_THEME_NAME || userSettings.themeMode !== DEFAULT_THEME_MODE) {
      const timeoutId = setTimeout(() => {
        saveThemeSettings();
      }, 1000); // Debounce saves

      return () => clearTimeout(timeoutId);
    }
  }, [userSettings, saveThemeSettings]);

  const contextValue: ThemeContextType = {
    currentTheme,
    themeMode,
    themeSelection,
    userSettings,
    availableThemes,
    setTheme,
    setThemeMode: setMode,
    setWorkspaceThemeOverride,
    saveThemeSettings,
    loadThemeSettings,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <ConfigProvider
        theme={currentTheme.antTheme}
      >
        {children}
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};

// Utility hooks
export const useThemeColors = () => {
  const { currentTheme } = useTheme();
  return currentTheme.colors;
};

export const useIsDark = () => {
  const { themeMode } = useTheme();
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkDark = () => {
      if (themeMode === 'dark') {
        setIsDark(true);
      } else if (themeMode === 'auto') {
        setIsDark(window.matchMedia('(prefers-color-scheme: dark)').matches);
      } else {
        setIsDark(false);
      }
    };

    checkDark();

    if (themeMode === 'auto') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      mediaQuery.addEventListener('change', checkDark);
      return () => mediaQuery.removeEventListener('change', checkDark);
    }
  }, [themeMode]);

  return isDark;
};